import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import {
  USER_INFO_UPDATED_EVENT,
  readToken,
  readUserInfo,
  writeUserInfo,
  clearUserInfoStorage,
  refreshUserInfoCache,
} from '../utils/userInfo';

const getApprovalLogTime = (log) => log?.updateTime || log?.createTime || log?.regTime || '';

const getLatestApprovalLog = (userInfo) => {
  const registerLogList = Array.isArray(userInfo?.registerLogList) ? userInfo.registerLogList : [];
  if (registerLogList.length === 0) return null;

  return [...registerLogList].sort((a, b) => (
    new Date(getApprovalLogTime(b)).getTime() - new Date(getApprovalLogTime(a)).getTime()
  ))[0];
};

/**
 * 全局用户信息 hook
 * 返回值：
 *   userInfo    - 当前用户信息对象，未登录时为 null
 *   isLoggedIn  - 是否已登录
 *   setUserInfo - 更新用户信息（同步写入 sessionStorage）
 *   clearUserInfo - 清除用户信息（退出登录）
 *   checkLogin  - 检查登录状态，如果传入 intercept 为 true，未登录或账户审核中时提示用户
 */
const useUserInfo = () => {
  const [userInfo, setUserInfoState] = useState(readUserInfo);
  const [isRefreshing, setIsRefreshing] = useState(() => Boolean(readToken()));

  useEffect(() => {
    const syncUserInfoState = () => {
      setUserInfoState(readUserInfo());
    };

    window.addEventListener('storage', syncUserInfoState);
    window.addEventListener(USER_INFO_UPDATED_EVENT, syncUserInfoState);

    return () => {
      window.removeEventListener('storage', syncUserInfoState);
      window.removeEventListener(USER_INFO_UPDATED_EVENT, syncUserInfoState);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const syncLatestUserInfo = async () => {
      if (!readToken()) {
        if (!cancelled) {
          setIsRefreshing(false);
          setUserInfoState(null);
        }
        return;
      }

      if (!cancelled) {
        setIsRefreshing(true);
      }

      const nextUserInfo = await refreshUserInfoCache({ force: true });

      if (!cancelled) {
        setUserInfoState(nextUserInfo);
        setIsRefreshing(false);
      }
    };

    syncLatestUserInfo();

    return () => {
      cancelled = true;
    };
  }, []);

  const setUserInfo = useCallback((info) => {
    writeUserInfo(info);
    setUserInfoState(info);
  }, []);

  const clearUserInfo = useCallback(() => {
    clearUserInfoStorage();
    setUserInfoState(null);
  }, []);

  const checkLogin = useCallback((intercept = false) => {
    // if (isRefreshing && readToken()) {
    //   return null;
    // }

    if (!userInfo?.id) {
      if (intercept) {
        message.warning('请先登录后再进行操作');
      }
      return null;
    }

    if (userInfo?.state != null && Number(userInfo.state) === 1) {
      return userInfo;
    }

    if (Number(userInfo?.userType) === 2 || userInfo?.roleCode === 'company') {
      const latestApprovalLog = getLatestApprovalLog(userInfo);
      const regState = Number(latestApprovalLog?.regState);

      if (regState === 2) {
        return userInfo;
      }

      if (regState === 3) {
        if (intercept) {
          message.warning('账户待补充材料，请前往账号设置查看详情');
        }
        return null;
      }

      if (latestApprovalLog) {
        if (intercept) {
          message.warning('账户审核中，请等待审核完成再进行操作');
        }
        return null;
      }
    }

    if (userInfo?.state != null && Number(userInfo.state) !== 1) {
      if (intercept) {
        message.warning('账户审核中，请等待审核完成再进行操作');
      }
      return null;
    }

    return userInfo;
  }, [isRefreshing, userInfo]);

  return {
    userInfo,
    isLoggedIn: !!userInfo,
    isRefreshing,
    setUserInfo,
    clearUserInfo,
    checkLogin,
  };
};

export default useUserInfo;
